import { Box, Container, Fade, Stack, styled } from '@mui/material';
import { ImageRatio } from '@synergy/models';
import { PoolImage, useComponentSize, useIsMobile } from '@synergy/components-next';
import { ReactElement, useEffect, useMemo, useState } from 'react';
import { TransitionGroup } from 'react-transition-group';
import { getHeightByRatio } from '@synergy/utils';

import { sponsorBannersSelector } from '../../../store/sponsor-banner-selectors';
import { useAppSelector } from '../../../../store/store';

const rotateArray = <T,>(array: T[], index: number): T[] => {
    const first = array.slice(0, index);
    const second = array.slice(index);
    return [...second, ...first];
};

const BANNER_INTERVAL = 5000;
const FADE_SPEED = 1000;

const SponsorImg = styled(PoolImage)(({ theme }) => ({
    display: 'block',
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    borderRadius: theme.spacing(0.5),
}));

export const SponsorBanner = (): ReactElement | null => {
    const banners = useAppSelector(sponsorBannersSelector);
    const bannerIndexes = useMemo(() => banners.map((_, index) => index), [banners]);
    const isMobile = useIsMobile();
    const [index, setIndex] = useState(0);
    const { measuredRef, width } = useComponentSize();

    // Rotate the banners every BANNER_INTERVAL seconds
    useEffect(() => {
        const interval = setInterval(() => {
            let nextIndex = index + 2;
            if (nextIndex >= banners.length) {
                nextIndex = nextIndex - banners.length;
            }
            setIndex(nextIndex);
        }, BANNER_INTERVAL);
        return () => clearInterval(interval);
    });

    // Sadly we need to position the banners absolutely to fade them over and the parent
    // div won't have a height if we don't set it explicitly
    // The height is the container width with a 2:1 ratio of the image minus 16px for the padding
    const height = useMemo(() => {
        let height = getHeightByRatio(ImageRatio.TwoByOne, width);
        if (isMobile) {
            // In mobile view we need to double the height because we have 2 banners above and below
            // Special handling if only one banners is present
            if (banners.length === 1) {
                height = height - 16;
            } else {
                height = height * 2 - 16;
            }
        } else {
            // In desktop view we need to half the height because we have 2 banners side by side
            height = height / 2 - 16;
        }
        return height;
    }, [banners.length, isMobile, width]);

    // Get the indexes of the next 2 banners
    // and select the pairs of banners
    // We rotate the array to seamlessly start at the beginning of the array when we reach the end
    const bannerPair = useMemo(
        () =>
            rotateArray(bannerIndexes, index)
                .slice(0, 2)
                .map(bannerIndex => banners[bannerIndex]),
        [bannerIndexes, banners, index]
    );

    if (banners.length === 0) {
        return null;
    }

    return (
        <Container maxWidth="xl" ref={measuredRef} sx={{ height, mt: { xs: 0, sm: 3 }, mb: { xs: 4, sm: 6.5 } }}>
            <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
                <TransitionGroup>
                    <Fade key={bannerPair[0].key} timeout={FADE_SPEED}>
                        <Stack
                            flexDirection={isMobile ? 'column' : 'row'}
                            gap={2}
                            sx={{ position: 'absolute', width: '100%', height: '100%' }}
                        >
                            {bannerPair.map(item => (
                                <Box key={item.key} sx={{ width: '100%', height: { xs: '50%', sm: '100%' } }}>
                                    <a href={item.url} target="_blank" rel="noreferrer">
                                        <SponsorImg src={item?.image['740x370']} alt="banner" />
                                    </a>
                                </Box>
                            ))}
                        </Stack>
                    </Fade>
                </TransitionGroup>
            </Box>
        </Container>
    );
};
